import React, { useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import classNames from 'classnames'
import { useSelector, useDispatch } from 'react-redux'
import { isArray, isEmpty, sortBy } from 'lodash'

import { IconArrowUp, IconArrowDown, IconX } from '@tabler/icons-react'
import { fetchPlaybookForCallExplorer } from '@/reducers/callSearch/callSearch.actions'
import { updateMediaPlayer } from '@/reducers/media/media.redux'

import { EventBlock } from './EventBlock'
import { AgentActivityBlock } from './AgentActivityBlock'
import {
  AGENT_ACTIVITY_SECTIONS,
  HARD_SELECTED_EVENT_ID,
  EVENT_VIEWER_ID,
  SECTION_NAMES,
  getContextFromEvent,
  updateCallExplorerParams,
  parseCallExplorerParams,
} from './helpers'
import { EventFilters } from './EventFilters'

import './EventsViewer.scss'

const TRIGGER_OFFSET_FROM_CONTAINER = 16

export const EventsViewer = ({
  analysis,
  highlightedEvents = [],
  copilotAnchors,
  dynamicInsightsAnchors,
  agentActivityEvents = [],
  metadata,
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { config_id: playbookId } = metadata || {}
  const {
    callExplorer: { playbook },
  } = useSelector((state) => state.callSearch)

  // Array.prototype.sort is stable, which is load-bearing here and not just tidy:
  // several tool calls can share one turn_start_time, and the order they arrive in
  // is the order the agent actually ran them. Re-sorting them by anything else, or
  // relying on an unstable sort, would misreport the sequence.
  const allEvents = [
    ...analysis,
    ...copilotAnchors,
    ...dynamicInsightsAnchors,
    ...agentActivityEvents,
  ].sort((a, b) => a.timestamp - b.timestamp)

  const filterOptions = [...new Set(analysis?.map((item) => item.section))].map((section) => ({
    id: section,
    label: SECTION_NAMES[section],
  }))
  if (copilotAnchors.length) {
    filterOptions.push({ id: 'qa_copilot', label: 'Quality' })
  }
  if (dynamicInsightsAnchors.length) {
    filterOptions.push({ id: 'insight', label: 'Insights' })
  }
  // Only offer a chip for a kind that is actually present, so a call with tool
  // calls but no events (or neither) shows no filter for what it doesn't have.
  AGENT_ACTIVITY_SECTIONS.forEach((section) => {
    if (agentActivityEvents.some((event) => event.section === section)) {
      filterOptions.push({ id: section, label: SECTION_NAMES[section] })
    }
  })

  function getSpokenChecklistItems() {
    return (
      analysis
        ?.filter((event) => event.section === 'checklist')
        .map((event) => {
          return event.item || event.context
        }) || []
    )
  }

  function getUnspokenChecklistItems() {
    const spokenItems = getSpokenChecklistItems()
    const allChecklistItems = playbook?.body?.checklist?.order || []

    return allChecklistItems.filter((entryId) => {
      const checklistItem = playbook?.body?.checklist?.entries?.[entryId]
      if (!checklistItem || !checklistItem.name) return false

      return !spokenItems.some((spokenItem) => {
        if (!spokenItem) return false
        const spokenLower = spokenItem.toLowerCase()
        const checklistLower = checklistItem.name.toLowerCase()
        return spokenLower.includes(checklistLower) || checklistLower.includes(spokenLower)
      })
    })
  }
  // if there were no spoken checklist items, but there is a checklist in the playbook, add checklist filter option
  if (
    !isEmpty(getUnspokenChecklistItems()) &&
    !filterOptions.some((option) => option.id === 'checklist')
  ) {
    filterOptions.push({ id: 'checklist', label: 'Checklist' })
  }
  filterOptions.unshift({ id: 'all', label: 'All Events' })

  const [filtersApplied, setFiltersApplied] = useState(filterOptions.map((option) => option.id))
  const [filterLabel, setFilterLabel] = useState('all')
  const [carouselVisible, setCarouselVisible] = useState(true)
  const [highlightedEventIndex, setHighlightedEventIndex] = useState(0)

  const isChecklistFilterApplied = filterLabel === 'checklist'

  const { mediaPlayer } = useSelector((state) => state.media)
  const { hardSelectedEvent } = mediaPlayer

  useEffect(() => {
    const myElement = document.getElementById(HARD_SELECTED_EVENT_ID)
    const triggerContainerScroll = hardSelectedEvent && myElement

    if (triggerContainerScroll) {
      const topPos = myElement.offsetTop - TRIGGER_OFFSET_FROM_CONTAINER

      document.getElementById(EVENT_VIEWER_ID).scrollTo({ top: topPos, behavior: 'smooth' })
    }
  }, [hardSelectedEvent])

  const handleFilterSelect = (selectedSection) => {
    updateCallExplorerParams(location, history, { eventFilter: selectedSection.id })
    setFilterLabel(selectedSection.id)

    if (selectedSection.id === 'all') {
      setFiltersApplied(filterOptions.map((option) => option.id))
      return
    }

    if (selectedSection.id === 'checklist' && playbookId) {
      dispatch(fetchPlaybookForCallExplorer(playbookId))
    }

    setFiltersApplied([selectedSection.id])
  }

  useEffect(() => {
    const { eventFilter, eventId, timestamp, keywords, events } = parseCallExplorerParams(location)

    if (eventFilter) {
      handleFilterSelect({ id: eventFilter })
    }

    const isEventSelected = eventId && timestamp
    if (isEventSelected) {
      dispatch(
        updateMediaPlayer({ hardSelectedEvent: { id: eventId, timestamp: Number(timestamp) } })
      )
    }
    if (!isEventSelected && keywords) {
      const firstKeywordEvent = allEvents.find((event) => event.section === 'keywords')

      if (!isEmpty(firstKeywordEvent)) {
        const { id, timestamp: kwTimestamp } = firstKeywordEvent

        dispatch(updateMediaPlayer({ hardSelectedEvent: { id, timestamp: kwTimestamp } }))
      }
    }
    if (!isEventSelected && events) {
      const firstEvent = allEvents.find((event) => {
        const eventContext = getContextFromEvent(event)

        if (!eventContext) {
          return false
        }
        if (isArray(events)) {
          return events.includes(eventContext)
        }
        return events === eventContext
      })

      if (!isEmpty(firstEvent)) {
        const { id, timestamp: eventTimestamp } = firstEvent

        dispatch(updateMediaPlayer({ hardSelectedEvent: { id, timestamp: eventTimestamp } }))
      }
    }
  }, [])

  const selectHighlightedEvent = (newIndex) => {
    const formattedIndex = Math.abs(newIndex % highlightedEvents.length)
    const newHardSelectedEvent = highlightedEvents[formattedIndex]

    dispatch(updateMediaPlayer({ hardSelectedEvent: newHardSelectedEvent }))
    setHighlightedEventIndex(formattedIndex)
    updateCallExplorerParams(location, history, {
      eventId: newHardSelectedEvent.id,
      timestamp: newHardSelectedEvent.timestamp,
    })
  }

  const handleNextHighlightedEvent = (event) => {
    event.preventDefault()
    selectHighlightedEvent(highlightedEventIndex + 1)
  }

  const handlePrevHighlightedEvent = (event) => {
    event.preventDefault()
    const prevIndex = highlightedEventIndex - 1
    const nextIndex = prevIndex >= 0 ? prevIndex : highlightedEvents.length - 1
    selectHighlightedEvent(nextIndex)
  }

  const closeCarousel = (event) => {
    event.preventDefault()
    setCarouselVisible(false)
  }

  const analysisFiltered = allEvents?.filter(
    (entry) =>
      filtersApplied.includes(entry?.section) ||
      (filtersApplied.includes('qa_copilot') && entry.criteria_name && entry.scorecard_name)
  )

  const highlightedFiltered = highlightedEvents.filter((entry) =>
    filtersApplied.includes(entry.section)
  )
  const isCarouselVisible = carouselVisible && !isEmpty(highlightedFiltered)

  const isArrowDisabled = !(highlightedFiltered.length > 1)
  const arrowClasses = classNames({
    'arrow-disabled': isArrowDisabled,
  })

  return (
    <>
      <EventFilters
        title="Filters"
        // 'All Events' is the reset option and is unshifted first above, but the
        // alphabetical sort here used to undo that only by luck — it led because no
        // existing label sorted before it. 'Agent Event' does, so pin it explicitly.
        filterOptions={sortBy(filterOptions, (option) => (option.id === 'all' ? '' : option.label))}
        selectedFilters={filterLabel}
        handleFilterSelect={handleFilterSelect}
      />
      {isCarouselVisible && (
        <div className="events-viewer-highlighted-carousel">
          <span>{`${highlightedEventIndex + 1} of ${highlightedFiltered.length}`}</span>
          <IconArrowDown className={arrowClasses} onClick={handleNextHighlightedEvent} />
          <IconArrowUp className={arrowClasses} onClick={handlePrevHighlightedEvent} />
          <IconX onClick={closeCarousel} />
        </div>
      )}
      <div id={EVENT_VIEWER_ID} className="events-scrollable-container" data-testid="event-blocks">
        {analysisFiltered?.map((event) => {
          if (AGENT_ACTIVITY_SECTIONS.includes(event.section)) {
            return <AgentActivityBlock key={`event-${event.id}`} event={event} />
          }

          const contextStr = getContextFromEvent(event)
          const highlighted = highlightedFiltered.some((hEvent) => {
            const eventContext = getContextFromEvent(hEvent)
            return eventContext === contextStr
          })

          return (
            <EventBlock
              key={`event-${event.id || `${event.criteria_name}-${event.scorecard_name}`}`}
              event={event}
              highlighted={highlighted}
            />
          )
        })}

        {isChecklistFilterApplied && playbookId && playbook?.body?.checklist && (
          <div className="checklist-divider" />
        )}

        {/* missed checklist items */}
        {isChecklistFilterApplied &&
          playbookId &&
          playbook?.body?.checklist &&
          getUnspokenChecklistItems().map((entryId) => {
            const checklistItem = playbook.body.checklist.entries[entryId]
            return (
              <div key={entryId} className="event-block">
                <div className="event-detail-container">
                  <div className="event-item">
                    <span className="section-category-label event-section-missed-checklist">
                      Missed Checklist
                    </span>
                    <div className="event-content">{checklistItem.name}</div>
                  </div>
                </div>
              </div>
            )
          })}
      </div>
    </>
  )
}
